import React, { useContext, useState, useEffect, useMemo } from 'react'
import {
  Box,
  Typography,
  Paper,
  AppBar,
  Toolbar,
  IconButton,
  Switch,
  Drawer,
  useMediaQuery,
  Fab,
  Tooltip,
  Tabs,
  Tab,
  CircularProgress,
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import { useAlertContext } from '../contexts/AlertContext'
import AlertHistory from './AlertHistory'
import EditTab from './tabs/EditTab'
import { Pattern } from '../../builder/contexts/PatternContext'
import PatternVersionTitle from '../../../components/backtests/PatternVersionTitle'
import { useApplicationSuiteContext } from '../../../contexts/ApplicationSuiteProvider'
import AlertConditionStatus from './AlertConditionStatus'
import AccountTreeIcon from '@mui/icons-material/AccountTree'
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive'
import NotificationsIcon from '@mui/icons-material/Notifications'
import SettingsIcon from '@mui/icons-material/Settings'
import CloseIcon from '@mui/icons-material/Close'
import SyncIcon from '@mui/icons-material/Sync'
import { doc, getDoc, updateDoc } from 'firebase/firestore'
import { firebase_firestore, firebase_functions } from '../../../common/firebaseConfig'
import { Tabs as AppTabs } from '../../../layout/MainLayout'
import { isPatternOwner } from '../../../common/utils/isPatternOwner'
import { useCopyPatternToAccount } from '../../../hooks/useCopyPatternToAccount'
import AssessmentIcon from '@mui/icons-material/Assessment'
import FileCopyIcon from '@mui/icons-material/FileCopy'
import { GreenChartTooltip } from '../../../common/components/GreenChartTooltip'
import { SnackbarHelperContext } from '../../../contexts/SnackbarHelperContext'
import { COMPOSITE_GATE_MESSAGE, compositeGateActive } from '../../../common/utils/composite'
import BacktestEquityChart from '../../../components/backtests/BacktestEquityChart'
import TradeDurationChart from '../../../components/backtests/TradeDurationChart'
import TradeTimeAnalysis from '../../../components/backtests/TradeTimeAnalysis'
import { useHttpsCallable } from 'react-firebase-hooks/functions'
import { useDocumentData } from 'react-firebase-hooks/firestore'
import { captureAlertActiveToggled } from '../../../common/analytics/alertEvents'

interface TabPanelProps {
  children?: React.ReactNode
  index: number
  value: number
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`insights-tabpanel-${index}`}
      aria-labelledby={`insights-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  )
}

function a11yProps(index: number) {
  return {
    id: `insights-tab-${index}`,
    'aria-controls': `insights-tabpanel-${index}`,
  }
}

const AlertEditView: React.FC = () => {
  const theme = useTheme()
  const { setSnackbarSettings } = useContext(SnackbarHelperContext)
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))

  const { usermeta, canViewPrivatePatterns, canActivateAlerts, setActiveTab } =
    useApplicationSuiteContext()

  const [getInitialAccountEquity, loadingInitialAccountEquity] = useHttpsCallable(
    firebase_functions,
    'getInitialAccountEquity'
  )

  const { exitEditMode, openSidePanel, selectedAlert: alert, setSelectedAlert } = useAlertContext()

  const exitTradingPlanDocRef = useMemo(() => {
    const id = alert?.trading?.exitTradingPlanId
    return id ? doc(firebase_firestore, 'tradingPlans', id) : null
  }, [alert?.trading?.exitTradingPlanId])

  const [exitTradingPlan] = useDocumentData(exitTradingPlanDocRef as any)

  const backtestDocRef = useMemo(() => {
    return alert?.from === 'backtest' && alert?.backtestId && alert?.backtestGcid
      ? doc(firebase_firestore, `usermeta/${alert.backtestGcid}/backtests/${alert.backtestId}`)
      : null
  }, [alert?.from, alert?.backtestId, alert?.backtestGcid])

  const [backtestData] = useDocumentData(backtestDocRef as any)

  const { copyPattern } = useCopyPatternToAccount()

  const [isPanelOpen, setIsPanelOpen] = useState(true)
  const [mounted, setMounted] = useState(false)
  const [refreshTrigger, setRefreshTrigger] = useState(0)
  const [insightsTabValue, setInsightsTabValue] = useState(0)
  const [accountEquity, setAccountEquity] = useState(0)

  // Handle mounting for animation
  useEffect(() => {
    if (isPanelOpen) {
      setMounted(true)
    }
  }, [isPanelOpen])

  useEffect(() => {
    if (alert?.trading?.accountId) {
      getInitialAccountEquity({ accountId: alert.trading.accountId })
        .then((result: any) => {
          setAccountEquity(result.data.data)
        })
        .catch((error: any) => {
          console.error('Error getting initial account equity:', error)
        })
    }
  }, [alert?.trading?.accountId, getInitialAccountEquity])

  const handleOpenPanel = () => {
    setIsPanelOpen(true)
  }

  const handleClosePanel = () => {
    setIsPanelOpen(false)
  }

  const handleInsightsTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setInsightsTabValue(newValue)
  }

  if (!alert) return null

  // Function to toggle alert active status
  const toggleActive = async () => {
    if (!alert || !alert.id || (!alert.active && !canActivateAlerts)) return

    const newActiveState = !alert.active

    // Composite-bearing patterns can't be evaluated by the alert engine yet
    if (newActiveState && alert.pattern && compositeGateActive(alert.pattern)) {
      setSnackbarSettings({ severity: 'info', message: COMPOSITE_GATE_MESSAGE })
      return
    }

    try {
      const alertRef = doc(firebase_firestore, 'tradingPlans', alert.id)
      await updateDoc(alertRef, {
        active: newActiveState,
      })
      captureAlertActiveToggled(alert, newActiveState, 'edit_view')

      setSelectedAlert({
        ...alert,
        active: newActiveState,
      })

      setSnackbarSettings({
        severity: 'success',
        message: `Alert ${newActiveState ? 'activated' : 'deactivated'} successfully`,
        autoHideDuration: 3000,
        anchorOrigin: { vertical: 'bottom', horizontal: 'center' },
      })
    } catch (error) {
      console.error('Error updating alert active state:', error)

      setSnackbarSettings({
        severity: 'error',
        message: `Failed to ${newActiveState ? 'activate' : 'deactivate'} alert`,
        autoHideDuration: 5000,
        anchorOrigin: { vertical: 'bottom', horizontal: 'center' },
      })
    }
  }

  const handleBack = () => {
    if (alert) {
      exitEditMode()
      setTimeout(() => {
        openSidePanel(alert)
      }, 50)
    } else {
      exitEditMode()
    }
  }

  const handleRefreshHistory = () => {
    setRefreshTrigger((prev) => prev + 1)
  }

  const symbol = alert.pattern!.trigger?.chartDetails?.symbol || 'Unknown'
  const canViewPatternDetails = canViewPrivatePatterns || isPatternOwner(alert.pattern!, usermeta)

  const handleViewPattern = () => {
    setActiveTab({ tab: AppTabs.PatternBuilder, options: { item: alert.pattern } })
  }

  const canViewExitPattern =
    exitTradingPlan?.pattern &&
    (canViewPrivatePatterns || isPatternOwner(exitTradingPlan.pattern, usermeta))

  const handleViewExitPattern = () => {
    if (exitTradingPlan?.pattern) {
      setActiveTab({ tab: AppTabs.PatternBuilder, options: { item: exitTradingPlan.pattern } })
    }
  }

  const handleCopyPattern = async () => {
    if (!alert.pattern) return
    await copyPattern(alert.pattern)
  }

  const handleViewBacktest = async () => {
    if (!alert.backtestId || !alert.backtestGcid) return
    try {
      const backtestRef = doc(
        firebase_firestore,
        `usermeta/${alert.backtestGcid}/backtests/${alert.backtestId}`
      )
      const snap = await getDoc(backtestRef)
      if (snap.exists()) {
        setActiveTab({ tab: AppTabs.Showcase, options: { item: { id: snap.id, ...snap.data() } } })
      } else {
        setSnackbarSettings({
          message: 'Source backtest is no longer available',
          severity: 'warning',
          autoHideDuration: 4000,
        })
      }
    } catch {
      setSnackbarSettings({
        message: 'Source backtest is no longer available',
        severity: 'warning',
        autoHideDuration: 4000,
      })
    }
  }

  // Panel content for both mobile drawer and desktop fixed panel
  const panelContent = (
    <Paper
      elevation={4}
      sx={{
        position: 'relative',
        width: isMobile ? '100%' : 400,
        height: '100%',
        backgroundColor: theme.palette.background.custom2,
        borderLeft: isMobile ? 'none' : `1px solid ${theme.palette.divider}`,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          backgroundColor: theme.palette.background.custom4 || '#1e1e1e',
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            overflow: 'hidden',
          }}
        >
          {alert.active ? <NotificationsActiveIcon /> : <NotificationsIcon />}
          <Typography
            variant="h6"
            sx={{
              fontWeight: 'bold',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
            }}
          >
            Edit Alert
          </Typography>
        </Box>
        {isMobile && (
          <IconButton
            onClick={handleClosePanel}
            size="small"
            sx={{
              color: theme.palette.text.secondary,
              '&:hover': {
                color: theme.palette.text.primary,
                backgroundColor: 'rgba(255, 255, 255, 0.08)',
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        )}
      </Box>

      {/* Edit Tab Content */}
      <EditTab alert={alert} />
    </Paper>
  )

  return (
    <Box sx={{ display: 'flex', overflow: 'hidden', flexDirection: 'column', height: '100%' }}>
      {/* Content */}
      <Box display={'flex'} overflow={'hidden'} width={'100%'} height={'100%'}>
        <Box
          sx={{
            flex: 1,
            overflowY: 'hidden',
            width: isMobile ? '100%' : 'calc(100% - 400px)',
          }}
        >
          {/* Header */}
          <AppBar
            position="static"
            sx={{
              boxShadow: 'none',
              background: 'none',
              borderBottom: '1px solid rgba(255, 255, 255, 0.06)',
            }}
          >
            <Toolbar>
              <IconButton
                edge="start"
                color="inherit"
                onClick={handleBack}
                sx={{ mr: 2 }}
                aria-label="back to alerts list"
              >
                <ArrowBackIcon />
              </IconButton>

              {alert ? (
                <Box sx={{ width: '100%' }}>
                  <Box
                    display={'flex'}
                    justifyContent={'space-between'}
                    width={'100%'}
                    alignItems={'center'}
                  >
                    <Box overflow={'hidden'} display={'flex'} alignItems={'center'} gap={1}>
                      {alert.active ? (
                        <NotificationsActiveIcon fontSize={'small'} />
                      ) : (
                        <NotificationsIcon fontSize={'small'} />
                      )}
                      <Typography
                        variant="body1"
                        color="text.primary"
                        overflow="hidden"
                        whiteSpace="nowrap"
                        textOverflow="ellipsis"
                      >
                        {alert.name}
                      </Typography>
                    </Box>

                    {/* Alert activation switch */}
                    <Box display="flex" alignItems="center">
                      <GreenChartTooltip
                        title={
                          alert.active
                            ? 'This alert is active, click to deactivate'
                            : 'This alert is inactive, click to activate'
                        }
                        arrow
                        placement="bottom"
                      >
                        <Switch
                          disabled={!alert.active && !canActivateAlerts}
                          checked={alert.active || false}
                          onChange={toggleActive}
                          color="primary"
                          size={'small'}
                        />
                      </GreenChartTooltip>
                    </Box>
                  </Box>

                  {/* Entry Pattern Row */}
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      width: '100%',
                    }}
                  >
                    <Box
                      display="flex"
                      alignItems="center"
                      gap={1}
                      sx={{ overflow: 'hidden', flex: 1, minWidth: 0 }}
                    >
                      <Tooltip
                        title={
                          canViewPatternDetails ? 'View Pattern' : 'Copy pattern to your account'
                        }
                        placement="bottom"
                      >
                        <Box
                          display={'flex'}
                          alignItems={'center'}
                          gap={1}
                          onClick={canViewPatternDetails ? handleViewPattern : handleCopyPattern}
                          sx={{
                            overflow: 'hidden',
                            cursor: 'pointer',
                            borderRadius: 1,
                            px: 0.5,
                            mx: -0.5,
                            flexShrink: 0,
                            '&:hover': { backgroundColor: 'rgba(255,255,255,0.06)' },
                          }}
                        >
                          <AccountTreeIcon
                            fontSize={'small'}
                            sx={{ color: theme.palette.text.secondary, flexShrink: 0 }}
                          />
                          <PatternVersionTitle
                            pattern={alert.pattern as Pattern}
                            symbol={symbol}
                            color="text.secondary"
                          />
                        </Box>
                      </Tooltip>
                      {alert.active && (
                        <AlertConditionStatus
                          pattern={alert.pattern as Pattern}
                          isActive={true}
                          inline
                        />
                      )}
                    </Box>
                    <Box display="flex" alignItems="center" gap={0.5}>
                      {!canViewPatternDetails && (
                        <Tooltip title="Copy pattern to your account" placement="bottom">
                          <IconButton
                            size="small"
                            onClick={handleCopyPattern}
                            sx={{ color: 'text.secondary' }}
                          >
                            <FileCopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </Box>

                  {/* Exit Pattern Row */}
                  {exitTradingPlan?.pattern && (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        width: '100%',
                        mt: 0.5,
                      }}
                    >
                      <Box
                        display="flex"
                        alignItems="center"
                        gap={1}
                        sx={{ overflow: 'hidden', flex: 1, minWidth: 0 }}
                      >
                        <Tooltip
                          title={
                            canViewExitPattern
                              ? 'View Exit Pattern'
                              : 'Copy pattern to your account'
                          }
                          placement="bottom"
                        >
                          <Box
                            display={'flex'}
                            alignItems={'center'}
                            gap={1}
                            onClick={canViewExitPattern ? handleViewExitPattern : handleCopyPattern}
                            sx={{
                              overflow: 'hidden',
                              cursor: 'pointer',
                              borderRadius: 1,
                              px: 0.5,
                              mx: -0.5,
                              flexShrink: 0,
                              '&:hover': { backgroundColor: 'rgba(255,255,255,0.06)' },
                            }}
                          >
                            <AccountTreeIcon
                              fontSize={'small'}
                              sx={{ color: theme.palette.text.secondary, flexShrink: 0 }}
                            />
                            <PatternVersionTitle
                              pattern={exitTradingPlan.pattern as Pattern}
                              symbol={
                                exitTradingPlan.pattern.trigger?.chartDetails?.symbol || 'Unknown'
                              }
                              color="text.secondary"
                            />
                          </Box>
                        </Tooltip>
                        {exitTradingPlan.active && (
                          <AlertConditionStatus
                            pattern={exitTradingPlan.pattern as Pattern}
                            isActive={true}
                            inline
                          />
                        )}
                      </Box>
                    </Box>
                  )}

                  {/* Source Backtest Row */}
                  {alert.from === 'backtest' && alert.backtestId && backtestData && (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        width: '100%',
                      }}
                    >
                      <Tooltip title="View backtest" placement="bottom">
                        <Box
                          display={'flex'}
                          alignItems={'center'}
                          gap={1}
                          onClick={handleViewBacktest}
                          sx={{
                            overflow: 'hidden',
                            cursor: 'pointer',
                            borderRadius: 1,
                            px: 0.5,
                            mx: -0.5,
                            '&:hover': { backgroundColor: 'rgba(255,255,255,0.06)' },
                          }}
                        >
                          <AssessmentIcon
                            fontSize={'small'}
                            sx={{ color: theme.palette.text.secondary, flexShrink: 0 }}
                          />
                          <Typography
                            variant="body1"
                            color="text.secondary"
                            overflow="hidden"
                            whiteSpace="nowrap"
                            textOverflow="ellipsis"
                          >
                            {backtestData.backtestSettings?.name}
                          </Typography>
                        </Box>
                      </Tooltip>
                    </Box>
                  )}
                </Box>
              ) : (
                <Typography variant="h6" component="div" sx={{ flexGrow: 1, fontWeight: 'bold' }}>
                  Alert Not Found
                </Typography>
              )}
            </Toolbar>
          </AppBar>

          {/* Main Content Area */}
          <Box
            sx={{
              px: 1,
              overflow: 'auto',
              backgroundColor: theme.palette.background.custom1,
              display: 'flex',
              flexDirection: 'column',
              gap: 3,
              height: 'calc(100% - 64px)',
              pt: 2,
            }}
          >
            {/* Trade Performance Section - Only show if trading account exists */}
            {alert.trading?.accountId && (
              <>
                <Box
                  display={'flex'}
                  width={'100%'}
                  gap={3}
                  flexDirection={{ xs: 'column', lg: 'row' }}
                >
                  {/* Equity Chart */}
                  <Paper
                    elevation={0}
                    sx={{
                      p: 4,
                      backgroundColor: theme.palette.background.custom2,
                      borderRadius: 2,
                      flex: 1,
                      flexShrink: 0,
                    }}
                  >
                    <Typography variant="h6">Trade Performance</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 5 }}>
                      Overview of net profit performance
                    </Typography>
                    <Box width={'100%'} height={250} sx={{ position: 'relative' }}>
                      {loadingInitialAccountEquity || !accountEquity ? (
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            height: '100%',
                          }}
                        >
                          <CircularProgress />
                        </Box>
                      ) : (
                        <BacktestEquityChart
                          alertId={alert.id}
                          tradeGroups={[]}
                          accountEquity={accountEquity}
                          highlightTopGroup
                        />
                      )}
                    </Box>
                  </Paper>

                  {/* Alert History */}
                  <Paper
                    elevation={0}
                    sx={{
                      p: 4,
                      backgroundColor: theme.palette.background.custom2,
                      borderRadius: 2,
                      flex: 1,
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                      }}
                    >
                      <Typography variant="h6">Notification History</Typography>
                      <Tooltip title="Refresh history">
                        <IconButton
                          onClick={handleRefreshHistory}
                          size="small"
                          sx={{
                            color: 'primary.main',
                            '&:hover': {
                              backgroundColor: 'rgba(255, 255, 255, 0.05)',
                            },
                          }}
                        >
                          <SyncIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                    <Box
                      sx={{
                        height: 300,
                      }}
                    >
                      <AlertHistory
                        alertId={alert.id}
                        isActive={alert.active}
                        tradingAccountId={alert.trading?.accountId}
                        refreshTrigger={refreshTrigger}
                      />
                    </Box>
                  </Paper>
                </Box>

                {/* Trade Analysis Section */}
                <Paper
                  elevation={0}
                  sx={{
                    p: 4,
                    backgroundColor: theme.palette.background.custom2,
                    borderRadius: 2,
                  }}
                >
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h6">Trade Analysis</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                      Detailed analysis of trade performance
                    </Typography>
                  </Box>

                  {/* Analysis Tabs */}
                  <Box sx={{ width: '100%' }}>
                    <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                      <Tabs
                        value={insightsTabValue}
                        onChange={handleInsightsTabChange}
                        aria-label="trade insights tabs"
                        variant="scrollable"
                        scrollButtons="auto"
                      >
                        <Tab label="Time-based Analysis" {...a11yProps(0)} />
                        <Tab label="Duration Analysis" {...a11yProps(1)} />
                      </Tabs>
                    </Box>

                    <TabPanel value={insightsTabValue} index={0}>
                      {loadingInitialAccountEquity || !accountEquity ? (
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            height: '100%',
                          }}
                        >
                          <CircularProgress />
                        </Box>
                      ) : (
                        <TradeTimeAnalysis
                          alertId={alert.id}
                          accountEquity={accountEquity}
                          selectedGroup=""
                          timeIntervals={[
                            { interval: 1, timeframe: 'hour' },
                            {
                              interval: alert.pattern!.trigger!.chartDetails!.periodicity,
                              timeframe: alert.pattern!.trigger!.chartDetails!.timeframe as
                                | 'hour'
                                | 'minute',
                            },
                          ]}
                        />
                      )}
                    </TabPanel>

                    <TabPanel value={insightsTabValue} index={1}>
                      <TradeDurationChart alertId={alert.id} selectedGroup="" />
                    </TabPanel>
                  </Box>
                </Paper>
              </>
            )}

            {/* Alert History Section - Show when no trading account */}
            {!alert.trading?.accountId && (
              <Paper
                elevation={0}
                sx={{
                  p: 4,
                  backgroundColor: theme.palette.background.custom2,
                  borderRadius: 2,
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mb: 2,
                  }}
                >
                  <Typography variant="h6">Notification History</Typography>
                </Box>
                <AlertHistory
                  alertId={alert.id}
                  isActive={alert.active}
                  tradingAccountId={alert.trading?.accountId}
                  refreshTrigger={refreshTrigger}
                />
              </Paper>
            )}
          </Box>
        </Box>

        {/* Right Side Form Panel - Desktop Only */}
        {!isMobile && panelContent}

        {/* Mobile Drawer */}
        {isMobile && mounted && (
          <Drawer
            anchor="right"
            open={isPanelOpen}
            onClose={handleClosePanel}
            PaperProps={{
              sx: {
                width: '400px',
                maxWidth: '100%',
                height: '100%',
              },
            }}
          >
            {panelContent}
          </Drawer>
        )}

        {/* Mobile FAB to open panel */}
        {isMobile && (
          <Fab
            color="primary"
            aria-label="open settings"
            onClick={handleOpenPanel}
            sx={{
              position: 'fixed',
              bottom: 16,
              right: 16,
              zIndex: 1000,
            }}
          >
            <SettingsIcon />
          </Fab>
        )}
      </Box>
    </Box>
  )
}

export default AlertEditView
